/**
 * 公式の地区割当 (町丁目ごとに指定された広域避難場所) を物件の位置・住所から引く。
 *
 * - 面の割当 (東京 23 区: 都 第9回地区割当) — 位置を含む面で決まる
 * - 町丁目表の割当 (横浜市 表3.12 など) — 住所の区・町・丁目で決まる
 * 自動選定はこの結果を「最寄り」より優先する (最寄り ≠ 指定。R1)。
 * 決められないとき (町丁目の一部だけが指定・住所から区や町を読めない) は理由つきで返し、
 * 選定は従来の自動選定に委ねる。根拠 (basis) は避難先の sourceNote に残す。
 * 「割当無し」は区が表にあり町まで読めたのに表に無い町丁目だけ (読み違いを割当無しにしない)。
 *
 * 東京の面には「地区内残留地区」(districtStay) が含まれる — placeName は残留地区名で
 * あって避難場所名ではないため、割当ではなく「地区内残留地区」として区別して返す。
 */
import type { LngLat } from '@/types/property';
import { pointInsideRing } from '@/lib/geo/polygon-utils';
import { insideAnyHole } from '@/lib/geo/polygon-holes';
import { isTokyoSpecialWard, parsePropertyAddress } from '@/lib/knowledge/address-match';
import { normalizeFacilityName } from '@/lib/knowledge/normalize';

type Ring = Array<[number, number]>;

export interface AssignmentZone {
  no: number;
  placeName: string;
  rings: Ring[];
  holes?: Ring[];
  /** 地区内残留地区 (避難場所ではなく、この地区に留まる指定)。placeName は残留地区名。 */
  districtStay?: true;
}
export interface ZoneSet {
  basis: string;
  zones: AssignmentZone[];
}
export interface TownAssignment {
  code: string;
  placeName: string;
  ward: string;
  town: string;
  chome: number[] | null;
  partial: boolean;
}
export interface TownTable {
  city: string;
  basis: string;
  entries: TownAssignment[];
  /**
   * 表に無い町丁目を自治体がどう呼ぶか (例: 横浜市の表3.13「大規模延焼火災の恐れが低い地域」)。
   * 無ければ「割当無し」だけを根拠に書く (自治体の言葉を他市に流用しない)。
   */
  unassignedLabel?: string;
}
export interface AssignmentData {
  zoneSets: ZoneSet[];
  townTables: TownTable[];
}
export type AssignmentResult =
  | { kind: 'assigned'; placeName: string; basis: string }
  | { kind: 'ambiguous'; candidates: string[]; basis: string }
  | { kind: 'unassigned'; basis: string }
  | null;

export function lookupAssignment(
  data: AssignmentData,
  coords: LngLat,
  address: string,
): AssignmentResult {
  // 住所の区切りの空白 (「横浜市 鶴見区」) は区・町の読み取りを崩すので先に落とす
  const parsed = parsePropertyAddress(address.replace(/[\s\u3000]/g, ''));

  // 面は位置で決まる。住所の書き方に左右されないので先に見る。
  for (const set of data.zoneSets) {
    const zone = set.zones.find(
      (z) => z.rings.some((r) => pointInsideRing(coords, r)) && !insideAnyHole(coords, z.holes),
    );
    if (zone) {
      if (zone.districtStay) {
        return { kind: 'unassigned', basis: `${set.basis}: 地区内残留地区 (${zone.placeName})` };
      }
      return { kind: 'assigned', placeName: zone.placeName, basis: `${set.basis}: ${zone.placeName}` };
    }
  }
  const tokyoSet = data.zoneSets[0];
  if (tokyoSet && isTokyoSpecialWard(parsed)) {
    return { kind: 'unassigned', basis: `${tokyoSet.basis}: 割当無し (どの割当範囲にも入らない)` };
  }

  const table = data.townTables.find((t) => t.city === parsed.city || t.city === parsed.ward);
  if (!table) return null;
  // 区名・町名は表記が揺れる (保土ヶ谷区/保土ケ谷区) ので照合キーで比べる
  const ward = parsed.ward ? normalizeFacilityName(parsed.ward) : '';
  const town = parsed.town ? normalizeFacilityName(parsed.town) : '';
  // 「割当無し」は公式の判断に聞こえる。区が表に無い・町を読めない住所でそう書くと、
  // 読み違いが「指定の無い地域」に化ける。決められないとして自動選定に委ねる。
  if (!town || !table.entries.some((e) => normalizeFacilityName(e.ward) === ward)) {
    return { kind: 'ambiguous', candidates: [], basis: `${table.basis}: 住所から町丁目を特定できない (要確認)` };
  }
  const hits = table.entries.filter(
    (e) =>
      normalizeFacilityName(e.ward) === ward &&
      normalizeFacilityName(e.town) === town &&
      (e.chome === null || parsed.chome === undefined || e.chome.includes(parsed.chome)),
  );
  const names = [...new Set(hits.map((h) => h.placeName))];
  if (names.length === 0) {
    const label = table.unassignedLabel ? `割当無し (${table.unassignedLabel})` : '割当無し';
    return { kind: 'unassigned', basis: `${table.basis}: ${label}` };
  }
  const decided =
    names.length === 1 &&
    !hits.some((h) => h.partial) &&
    // 住所に丁目が無いのに表が丁目で分けている町は決められない
    !(parsed.chome === undefined && hits.some((h) => h.chome !== null));
  if (decided) return { kind: 'assigned', placeName: names[0]!, basis: `${table.basis}: ${names[0]}` };
  return {
    kind: 'ambiguous',
    candidates: names,
    basis: `${table.basis}: 町丁目の一部だけが指定 (${names.join('・')})`,
  };
}
